import type { ProgrammaticLandingPageConfig } from '../types'
import { KWP_SIZE_FACTS } from './kwpSizeFacts'

const FACTS = KWP_SIZE_FACTS[5]

export const KWP_5_PHOTOVOLTAIK_KOSTEN: ProgrammaticLandingPageConfig = {
  slug: '5-kwp-photovoltaik-kosten',
  lang: 'de',
  topicId: 'kwp-kosten-5',
  title: '5 kWp Photovoltaikanlage: Kosten & Richtwerte 2026',
  metaDescription:
    'Was kostet eine 5-kWp-Photovoltaikanlage? Dachfläche, Modulanzahl, Kostenaufschlüsselung und ' +
    'kostenloser Preisvergleich für kleinere Haushalte.',
  keywords: [
    '5 kWp Photovoltaik Kosten',
    '5 kWp Solaranlage Preis',
    'Photovoltaikanlage 5 kWp',
    'Was kostet eine 5 kWp Anlage',
    '5 kWp PV-Anlage Haushalt',
  ],
  eyebrow: 'Anlagengröße 5 kWp',
  h1: '5 kWp Photovoltaikanlage: Kosten und Richtwerte',
  intro:
    'Eine 5-kWp-Anlage ist die typische Einstiegsgröße für kleinere Haushalte ohne Wärmepumpe ' +
    'oder E-Auto. Hier finden Sie Richtwerte zu Dachfläche und Modulanzahl, eine Kostenaufschlüsselung ' +
    'und direkt darunter einen Rechner mit dem für 5 kWp vorbelegten Durchschnittspreis aus echten ' +
    'Handwerker-Angeboten.',
  calculatorPrefill: { kind: 'kwp', kwp: 5 },
  indexPolicy: 'index',
  sections: [
    { type: 'heading', text: 'Für wen eignet sich eine 5-kWp-Anlage?' },
    {
      type: 'paragraph',
      text:
        'Richtwert: 5 kWp passen typischerweise zu einem 2-3-Personen-Haushalt mit Grundverbrauch ' +
        'von etwa 3.500-4.000 kWh im Jahr, ohne Wärmepumpe oder Elektroauto. Kommt später ein ' +
        'zusätzlicher Verbraucher dazu, lohnt sich vorab ein Blick auf eine größere Stufe wie 8 ' +
        'oder 10 kWp, statt die Anlage nachträglich zu erweitern.',
    },
    { type: 'heading', text: 'Dachfläche und Module: das braucht eine 5-kWp-Anlage' },
    {
      type: 'paragraph',
      text:
        `Richtwert: rund ${FACTS.roofAreaM2} m² Dachfläche und ca. ${FACTS.moduleCount} Module bei ` +
        `marktüblichen ~440-Wp-Modulen. Der Jahresertrag liegt bei einer Südausrichtung ohne ` +
        `Verschattung bei etwa ${FACTS.annualYieldKwh.toLocaleString('de-DE')} kWh - bei anderer ` +
        'Ausrichtung oder Verschattung weniger. Eine genaue, dachspezifische Berechnung liefert der ' +
        'kostenlose PV-Rechner.',
    },
    { type: 'heading', text: 'Kostenbestandteile einer Photovoltaikanlage' },
    {
      type: 'list',
      items: [
        'Module: größter Einzelposten, meist rund ein Drittel der Gesamtkosten',
        'Wechselrichter: wandelt den erzeugten Gleichstrom in nutzbaren Wechselstrom um',
        'Montage/Arbeitsleistung: Befestigung auf dem Dach, Verkabelung',
        'Gerüst: abhängig von Dachhöhe und -zugänglichkeit',
        'Elektroinstallation: Zählerschrank-Anpassung, Anschluss ans Hausnetz',
        'Anmeldung: Netzbetreiber und Marktstammdatenregister',
      ],
    },
    {
      type: 'paragraph',
      text:
        'Bei kleineren Anlagen wie 5 kWp machen Anmeldung und Grundgerüst anteilig einen größeren ' +
        'Teil der Gesamtkosten aus als bei größeren Anlagen, da diese Posten nicht proportional mit ' +
        'der Anlagengröße mitwachsen. Der Preis pro kWp fällt deshalb bei 5 kWp im Schnitt etwas ' +
        'höher aus als bei 10 oder 15 kWp.',
    },
    { type: 'heading', text: 'Amortisation und Eigenverbrauch' },
    {
      type: 'paragraph',
      text:
        'Ohne Batteriespeicher liegt der Eigenverbrauchsanteil bei einer 5-kWp-Anlage typischerweise ' +
        'bei rund 25-35 %, mit passend dimensioniertem Speicher deutlich höher. Wie schnell sich die ' +
        'Anlage amortisiert, hängt von Ihrem individuellen Verbrauchsprofil, der aktuellen ' +
        'Einspeisevergütung und eventuellen Förderungen ab - Details dazu im Förder-Überblick, eine ' +
        'genaue Berechnung für Ihr Dach liefert der PV-Rechner.',
    },
  ],
  faq: [
    {
      q: 'Reicht eine 5-kWp-Anlage für ein Einfamilienhaus?',
      a: 'Für kleinere Haushalte ohne Wärmepumpe oder E-Auto in der Regel ja. Bei höherem oder absehbar steigendem Verbrauch ist eine größere Stufe wie 8 oder 10 kWp meist die bessere Wahl, statt später nachzurüsten.',
    },
    {
      q: 'Lohnt sich bei 5 kWp noch ein Batteriespeicher?',
      a: 'Ja, allerdings kleiner dimensioniert als bei größeren Anlagen. Ob sich der Speicher lohnt, hängt vor allem davon ab, wie viel Strom Sie tagsüber selbst verbrauchen - Handwerksbetriebe können das anhand Ihres Verbrauchsprofils einschätzen.',
    },
    {
      q: 'Wie viele Module brauche ich für 5 kWp?',
      a: `Richtwert: etwa ${FACTS.moduleCount} Module bei marktüblichen ~440-Wp-Modulen, je nach tatsächlich gewähltem Modultyp auch etwas mehr oder weniger.`,
    },
  ],
  primaryCta: { label: 'Anlagengröße für mein Dach kostenlos berechnen', action: 'link', href: '/pv-rechner' },
  secondaryCta: { label: 'Direkt Anfrage erstellen', action: 'customer' },
  relatedLinks: [
    { label: '8 kWp Photovoltaikanlage: Kosten & Richtwerte', href: '/8-kwp-photovoltaik-kosten' },
    { label: '10 kWp Photovoltaikanlage: Kosten & Richtwerte', href: '/10-kwp-photovoltaik-kosten' },
    { label: '12 kWp Photovoltaikanlage: Kosten & Richtwerte', href: '/12-kwp-photovoltaik-kosten' },
    { label: 'Was kostet eine Solaranlage? Preise & Einflussfaktoren im Überblick', href: '/solaranlage-kosten' },
    { label: 'Photovoltaik-Förderung 2026: Deutschland & Österreich', href: '/blog/photovoltaik-foerderung-2026' },
    { label: 'PV-Kostenrechner: Durchschnittspreis für Ihre Anlage berechnen', href: '/pv-kostenrechner' },
  ],
}
